import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QHeading, QBox, deprecated } from '@qualio/ui-components';
import { EventTemplateStepCreateEdit, EventTemplateFormValues } from '../../types';
import StepRowActions from './components/StepRowActions';
import { ClickPropagationBarrier } from '../../components';
import FormTemplateDrawer from './components/FormTemplateDrawer';
import ContentTemplateDrawer from './components/ContentTemplateDrawer';
import { getStepLabel } from '../../views/Templates/EventTemplate/components/EventTemplateFormStepsTable';
import i18n from '../../i18n/config';

type TemplateStepsListProps = {
  companyId: number;
  templateSteps: EventTemplateFormValues['steps'];
  setTemplateSteps: (steps: EventTemplateFormValues['steps']) => void;
  disableEdit: boolean;
  isTemplateReadonly: boolean;
  renameTemplateStep: (stepIndex: number) => void;
  changeValidationStepType: (stepIndex: number) => void;
};

const stepColumns = [
  {
    Header: i18n.t('common.labels.order'),
    accessor: 'order',
    width: '5%',
    disableSortBy: true,
  },
  {
    Header: i18n.t('common.labels.name'),
    accessor: 'name',
    width: '40%',
    disableSortBy: true,
  },
  {
    Header: i18n.t('common.labels.type'),
    accessor: 'type',
    width: '40%',
    disableSortBy: true,
  },
  {
    Header: '',
    accessor: 'actions',
    width: '15%',
    disableSortBy: true,
  },
] as const;

const TemplateStepsList = ({
  companyId,
  templateSteps,
  setTemplateSteps,
  disableEdit,
  isTemplateReadonly,
  renameTemplateStep,
  changeValidationStepType,
}: TemplateStepsListProps) => {
  const [selectedStep, setSelectedStep] = useState<EventTemplateStepCreateEdit | null>(null);
  const closeTaskDrawer = useCallback(() => {
    setSelectedStep(null);
  }, [setSelectedStep]);
  const deleteTemplateStep = useCallback(
    (step: EventTemplateStepCreateEdit) => {
      const stepIndex = step.order - 1;
      templateSteps.splice(stepIndex, 1);
      setTemplateSteps([...templateSteps]);
      closeTaskDrawer();
    },
    [closeTaskDrawer, setTemplateSteps, templateSteps],
  );

  const onStepClick = (step: EventTemplateStepCreateEdit) => {
    if (step.template_id || step.form_template_id) {
      setSelectedStep(step);
    }
  };

  const preventStepDelete = (step: EventTemplateStepCreateEdit) => {
    return step.order === templateSteps.length;
  };

  const { t } = useTranslation();

  const tableData: deprecated.QDataRow[] = useMemo(() => {
    return templateSteps.map((step: EventTemplateStepCreateEdit): deprecated.QDataRow => {
      return {
        order: step.order,
        name:
          ((step.type === 'document' && step.template_id) || (step.type === 'form' && step.form_template_id)) &&
          !disableEdit ? (
            <QBox color={'blue.500'} cursor={'pointer'} onClick={() => onStepClick(step)}>
              {step.label}
            </QBox>
          ) : (
            step.label
          ),
        type: getStepLabel(t, templateSteps, step),
        actions: (
          <ClickPropagationBarrier>
            <StepRowActions
              step={step}
              templateSteps={templateSteps}
              onDelete={deleteTemplateStep}
              setTemplateSteps={setTemplateSteps}
              renameTemplateStep={renameTemplateStep}
              changeValidationStepType={changeValidationStepType}
              disableEdit={disableEdit}
              isTemplateReadonly={isTemplateReadonly}
            />
          </ClickPropagationBarrier>
        ),
      };
    });
  }, [
    t,
    templateSteps,
    deleteTemplateStep,
    setTemplateSteps,
    renameTemplateStep,
    changeValidationStepType,
    disableEdit,
    isTemplateReadonly,
  ]);

  return (
    <>
      <QHeading size="md" mt={4} mb={4}>
        Steps
      </QHeading>
      <deprecated.QDataTable columns={stepColumns} data={tableData} hasPagination={false} />
      {selectedStep && selectedStep.type === 'document' && (
        <QBox data-cy="content-drawer-screen">
          <ContentTemplateDrawer
            key={selectedStep.template_id}
            onClose={closeTaskDrawer}
            onDelete={deleteTemplateStep}
            preventStepDelete={preventStepDelete}
            step={selectedStep}
            isTemplateReadonly={isTemplateReadonly}
          />
        </QBox>
      )}
      {selectedStep && selectedStep.type === 'form' && (
        <QBox data-cy="form-drawer-screen">
          <FormTemplateDrawer
            companyId={companyId}
            key={selectedStep.template_id}
            onClose={closeTaskDrawer}
            onDelete={deleteTemplateStep}
            preventStepDelete={preventStepDelete}
            step={selectedStep}
            isTemplateReadonly={isTemplateReadonly}
          />
        </QBox>
      )}
    </>
  );
};

export default TemplateStepsList;
